import { useMemo } from 'react'
import { Badge } from '@/components/ui/badge'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import {
	COMMITMENT_EXCEPTION_REASON_LABELS,
	COMMITMENT_EXCEPTION_STATUS_BADGE_CLASS,
	COMMITMENT_EXCEPTION_STATUS_BAR_CLASS,
	COMMITMENT_EXCEPTION_STATUS_LABELS,
} from '@/constants/commitment-exception'
import { cn } from '@/lib/utils'
import type {
	CommitmentExceptionReason,
	CommitmentExceptionStatus,
	PersonTimelineExceptionItem,
	PersonTimelineExceptions,
} from '@/types'
import {
	formatDate,
	formatDateToDisplay,
	parseStringToDateForDisplay,
} from '@/utils/formatDate'
import {
	TimelineEmptyState,
	TimelineSectionHeading,
} from '../person-timeline-modal'

const MS_PER_DAY = 86_400_000

// Day index (days since epoch) for a date string, ignoring timezone
function toDayIndex(dateStr: string) {
	const date = parseStringToDateForDisplay(dateStr)
	if (!date) return Number.NaN
	return Math.round(
		Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / MS_PER_DAY,
	)
}

function dayCountLabel(days: number) {
	return days === 1 ? '1 dia' : `${days} dias`
}

interface ExceptionsTimelineSectionProps {
	data: PersonTimelineExceptions
	period: { rangeStart: string; rangeEnd: string }
}

export function ExceptionsTimelineSection({
	data,
	period,
}: ExceptionsTimelineSectionProps) {
	const periodStart = toDayIndex(period.rangeStart)
	const periodEnd = toDayIndex(period.rangeEnd)
	const periodDays = Math.max(periodEnd - periodStart + 1, 1)

	const items = useMemo(
		() =>
			[...data.items].sort(
				(a, b) =>
					a.startDate.localeCompare(b.startDate) ||
					a.endDate.localeCompare(b.endDate),
			),
		[data.items],
	)

	// Approved days inside the period (union of overlapping exceptions)
	const coveredDays = useMemo(() => {
		const covered = new Set<number>()
		for (const item of items) {
			if (item.status !== 'APROVADO') continue
			const start = Math.max(toDayIndex(item.startDate), periodStart)
			const end = Math.min(toDayIndex(item.endDate), periodEnd)
			for (let day = start; day <= end; day++) covered.add(day)
		}
		return covered
	}, [items, periodStart, periodEnd])

	const coveragePct = Math.round((coveredDays.size / periodDays) * 100)

	if (items.length === 0) {
		return <TimelineEmptyState label="Nenhum abono registrado no período." />
	}

	const statusEntries = Object.entries(data.byStatus) as [
		CommitmentExceptionStatus,
		number,
	][]
	const reasonEntries = (
		Object.entries(data.byReason) as [CommitmentExceptionReason, number][]
	).sort((a, b) => b[1] - a[1])

	return (
		<div className="flex flex-col gap-6">
			<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
				<div className="rounded-lg border border-kaefer-light-silver bg-white p-3">
					<TimelineSectionHeading>Dias abonados</TimelineSectionHeading>
					<p className="mt-1 text-xl font-semibold text-kaefer-dark-grey">
						{coveredDays.size}
						<span className="text-sm font-normal text-muted-foreground">
							{' '}
							/ {periodDays}
						</span>
					</p>
					<div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-kaefer-light-silver/60">
						<div
							className="h-full rounded-full bg-kaefer-grass-green"
							style={{ width: `${coveragePct}%` }}
						/>
					</div>
					<p className="mt-1 text-xs text-muted-foreground">
						{coveragePct}% do período coberto por abonos aprovados
					</p>
				</div>

				<div className="rounded-lg border border-kaefer-light-silver bg-white p-3">
					<TimelineSectionHeading>Por status</TimelineSectionHeading>
					<div className="mt-2 flex flex-wrap gap-1.5">
						{statusEntries.map(([status, count]) => (
							<Badge
								key={status}
								className={COMMITMENT_EXCEPTION_STATUS_BADGE_CLASS[status]}
							>
								{COMMITMENT_EXCEPTION_STATUS_LABELS[status]} · {count}
							</Badge>
						))}
					</div>
				</div>

				<div className="rounded-lg border border-kaefer-light-silver bg-white p-3">
					<TimelineSectionHeading>Por motivo</TimelineSectionHeading>
					<div className="mt-2 flex flex-wrap gap-1.5">
						{reasonEntries.map(([reason, count]) => (
							<Badge
								key={reason}
								className="bg-kaefer-light-silver/70 text-kaefer-dark-grey"
							>
								{COMMITMENT_EXCEPTION_REASON_LABELS[reason] ?? reason} · {count}
							</Badge>
						))}
					</div>
				</div>
			</div>

			<div className="flex flex-col gap-2">
				<TimelineSectionHeading>Linha do tempo</TimelineSectionHeading>
				<div className="rounded-lg border border-kaefer-light-silver bg-white p-3">
					<div className="flex justify-between text-[11px] text-muted-foreground">
						<span>{formatDateToDisplay(period.rangeStart)}</span>
						<span>{formatDateToDisplay(period.rangeEnd)}</span>
					</div>

					<div className="mt-2 flex flex-col gap-1.5">
						<CoverageRow
							coveredDays={coveredDays}
							periodStart={periodStart}
							periodDays={periodDays}
						/>
						{items.map((item) => (
							<ExceptionBar
								key={item.id}
								item={item}
								periodStart={periodStart}
								periodEnd={periodEnd}
								periodDays={periodDays}
							/>
						))}
					</div>

					<div className="mt-3 flex flex-wrap gap-3 text-[11px] text-muted-foreground">
						{(
							Object.keys(
								COMMITMENT_EXCEPTION_STATUS_LABELS,
							) as CommitmentExceptionStatus[]
						).map((status) => (
							<span key={status} className="flex items-center gap-1">
								<span
									className={cn(
										'size-2 rounded-full',
										COMMITMENT_EXCEPTION_STATUS_BAR_CLASS[status],
									)}
								/>
								{COMMITMENT_EXCEPTION_STATUS_LABELS[status]}
							</span>
						))}
					</div>
				</div>
			</div>

			<div className="flex flex-col gap-2">
				<TimelineSectionHeading>Abonos ({data.total})</TimelineSectionHeading>
				<div className="rounded-lg border border-kaefer-light-silver">
					<Table className="table-fixed">
						<TableHeader>
							<TableRow className="bg-muted/30">
								<TableHead className="w-48">Período</TableHead>
								<TableHead className="w-20">Dias</TableHead>
								<TableHead className="w-36">Motivo</TableHead>
								<TableHead className="w-28">Status</TableHead>
								<TableHead>Solicitado por</TableHead>
								<TableHead>Revisado por</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{items.map((item) => (
								<TableRow key={item.id}>
									<TableCell className="whitespace-nowrap">
										{formatDateToDisplay(item.startDate)} –{' '}
										{formatDateToDisplay(item.endDate)}
									</TableCell>
									<TableCell className="text-muted-foreground">
										{toDayIndex(item.endDate) - toDayIndex(item.startDate) + 1}
									</TableCell>
									<TableCell>
										<Badge className="bg-kaefer-light-silver/70 text-kaefer-dark-grey">
											{COMMITMENT_EXCEPTION_REASON_LABELS[item.reason] ??
												item.reason}
										</Badge>
									</TableCell>
									<TableCell>
										<Badge
											className={
												COMMITMENT_EXCEPTION_STATUS_BADGE_CLASS[item.status]
											}
										>
											{COMMITMENT_EXCEPTION_STATUS_LABELS[item.status] ??
												item.status}
										</Badge>
									</TableCell>
									<TableCell className="whitespace-normal wrap-break-word">
										<ActorCell
											name={item.requestedByName}
											at={item.requestedAt}
										/>
									</TableCell>
									<TableCell className="whitespace-normal wrap-break-word">
										<ActorCell
											name={item.reviewedByName}
											at={item.reviewedAt}
										/>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			</div>
		</div>
	)
}

function CoverageRow({
	coveredDays,
	periodStart,
	periodDays,
}: {
	coveredDays: Set<number>
	periodStart: number
	periodDays: number
}) {
	return (
		<div className="flex items-center gap-2">
			<span className="w-24 shrink-0 text-[11px] font-medium text-kaefer-dark-grey">
				Cobertura
			</span>
			<div className="flex h-3 flex-1 gap-px overflow-hidden rounded-sm">
				{Array.from({ length: periodDays }, (_, offset) => {
					const day = periodStart + offset
					const covered = coveredDays.has(day)
					return (
						<div
							key={day}
							title={`${formatDateToDisplay(
								new Date(day * MS_PER_DAY).toISOString(),
							)} · ${covered ? 'abonado' : 'sem abono'}`}
							className={cn(
								'h-full flex-1',
								covered ? 'bg-kaefer-grass-green' : 'bg-kaefer-light-silver/60',
							)}
						/>
					)
				})}
			</div>
		</div>
	)
}

function ExceptionBar({
	item,
	periodStart,
	periodEnd,
	periodDays,
}: {
	item: PersonTimelineExceptionItem
	periodStart: number
	periodEnd: number
	periodDays: number
}) {
	const start = Math.max(toDayIndex(item.startDate), periodStart)
	const end = Math.min(toDayIndex(item.endDate), periodEnd)
	const left = ((start - periodStart) / periodDays) * 100
	const width = (Math.max(end - start + 1, 0) / periodDays) * 100
	const reasonLabel =
		COMMITMENT_EXCEPTION_REASON_LABELS[item.reason] ?? item.reason
	const range = `${formatDateToDisplay(item.startDate)} – ${formatDateToDisplay(item.endDate)}`

	return (
		<div className="flex items-center gap-2">
			<span
				className="w-24 shrink-0 truncate text-[11px] text-muted-foreground"
				title={reasonLabel}
			>
				{reasonLabel}
			</span>
			<div className="relative h-3 flex-1 rounded-sm bg-kaefer-light-silver/30">
				<div
					title={`${reasonLabel} · ${range} · ${COMMITMENT_EXCEPTION_STATUS_LABELS[item.status]}`}
					className={cn(
						'absolute inset-y-0 rounded-sm',
						COMMITMENT_EXCEPTION_STATUS_BAR_CLASS[item.status],
					)}
					style={{ left: `${left}%`, width: `${width}%` }}
				/>
			</div>
			<span className="hidden w-36 shrink-0 text-right text-[11px] text-muted-foreground sm:block">
				{range} ·{' '}
				{dayCountLabel(
					toDayIndex(item.endDate) - toDayIndex(item.startDate) + 1,
				)}
			</span>
		</div>
	)
}

function ActorCell({ name, at }: { name: string | null; at: string | null }) {
	if (!name) return <span className="text-muted-foreground">—</span>
	return (
		<div className="flex flex-col">
			<span className="text-kaefer-dark-grey">{name}</span>
			{at && (
				<span className="text-[11px] text-muted-foreground">
					{formatDate(at)}
				</span>
			)}
		</div>
	)
}
